import { Box } from "@mui/system";
import {
  Button,
  Card,
  Grid,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import React, { useState, useEffect } from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  IEnhancedTableProps,
  ISCOPageDetails,
  IScheduledOffering,
  IScheduledOfferingListMeta,
} from "utils/interfaces/scheduled-offering";
import {
  getPagePayloadValues,
  getPagePermissions,
  showLoader,
} from "utils/helper";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  MAX_INT_VALUE,
} from "utils/constants/constant";
import NoRecords from "components/NoRecords";
import { AppPages } from "utils/enums/app-pages";
import CustomPagination from "components/customPagination";
import {
  GetSCOListByCourseIdPayload,
  GetSCOListByCourseIdSuccessPayload,
} from "store/course/types";
import { ISCOListByCourseIdRequest } from "utils/interfaces/course";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { AppRoutings } from "utils/enums/app-routings";
import { ExportToCsv } from "export-to-csv";
import { getUpdatedSCOListSelector } from "store/course/selector";
import { AuthState } from "store/rootReducer";
import { AuthState as AuthType } from "store/auth/types";
import { getUserSelector } from "store/auth/selector";

const EnhancedTableHead = (props: IEnhancedTableProps) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof ISCOPageDetails) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

interface ISCOTableProps {
  courseId: number;
  getSCOListByCourseIdRequest: (payload: GetSCOListByCourseIdPayload) => {};
}

const SCOTable: React.FC<ISCOTableProps> = (props) => {
  const user = useSelector<AuthState, AuthType["user"]>(getUserSelector);
  const history = useHistory();
  const isEditAllowed = getPagePermissions(
    user?.permissions,
    AppPages.ScheduledOfferings
  )?.edit;

  const isCourseEditedChanged = useSelector(getUpdatedSCOListSelector);

  const [scheduledOfferingList, setScheduledOfferingList] =
    useState<IScheduledOffering[]>();
  const [scheduledOfferingListMeta, setScheduledOfferingListMeta] =
    useState<IScheduledOfferingListMeta>();
  const [page, setPage] = useState<string>(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [orderBy, setOrderBy] = useState<keyof ISCOPageDetails>("courseNo");

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(DEFAULT_PAGE_NO);
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = React.useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof ISCOPageDetails) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const getSCOListSuccess = (response: GetSCOListByCourseIdSuccessPayload) => {
    setScheduledOfferingList(response.courseOfferingList);
    setScheduledOfferingListMeta(response.courseOfferingListPagination);
  };

  const getSCOList = () => {
    const { getSCOListByCourseIdRequest, courseId } = props;
    if (getSCOListByCourseIdRequest && courseId) {
      showLoader();
      const values: ISCOListByCourseIdRequest = getPagePayloadValues(
        orderBy,
        order,
        pageNo,
        page
      );
      values.courseId = courseId;
      const payload = {
        values,
        callback: getSCOListSuccess,
      };

      getSCOListByCourseIdRequest(payload);
    }
  };

  useEffect(() => {
    const permission = getPagePermissions(
      user?.permissions,
      AppPages.ScheduledOfferings
    );
    if (permission?.view) {
      getSCOList();
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo, order, orderBy, isCourseEditedChanged]);

  const csvFileHeaders = [
    "Schedule Course Offering Details",
    "Course",
    "Date",
    "Term",
    "Educators",
    "No. Enrolled",
    "No. Attended",
    "Location",
  ];
  const getScheduleOfferingModifiedData = (list: IScheduledOffering[]) => {
    const data = list?.map((so: IScheduledOffering) => {
      return {
        "Schedule Course Offering Details": so.courseOfferingDetails,
        Course: so.courseDTO.courseNo,
        Date: so.firstSession,
        Term: so.termsDTO.termName,
        Educators: so.educators,
        "No. Enrolled": so.noEnrolled,
        "No. Attended": so.attendanceCount,
        Location: so.locationDTO.location,
      };
    });

    return data;
  };
  const onExportSCOListByCourseIdSuccess = (
    response: GetSCOListByCourseIdSuccessPayload
  ) => {
    const modifiedCourseData = getScheduleOfferingModifiedData(
      response.courseOfferingList
    );
    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers: csvFileHeaders,
      filename: "Schedule Course Offering List",
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(modifiedCourseData);
  };

  const getExportScheduledCourseOfferings = () => {
    const { getSCOListByCourseIdRequest, courseId } = props;

    if (getSCOListByCourseIdRequest && courseId) {
      showLoader();

      const values: ISCOListByCourseIdRequest = getPagePayloadValues(
        orderBy,
        order,
        DEFAULT_PAGE_NO,
        MAX_INT_VALUE
      );
      values.courseId = courseId;
      const payload = {
        values,
        callback: onExportSCOListByCourseIdSuccess,
      };

      getSCOListByCourseIdRequest(payload);
    }
  };

  return (
    <Grid item xs={12}>
      <Card>
        <Box className="table-card-header">
          <Box
            sx={{
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: "16px",
            }}
          >
            <Typography variant="h3">Schedule Course Offerings</Typography>
            <Button
              disabled={!scheduledOfferingList?.length}
              variant="outlined"
              className="btn-export"
              onClick={getExportScheduledCourseOfferings}
            >
              Export
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    maxWidth: "170px",
                    width: "170px",
                    minWidth: "170px",
                  }}
                >
                  Schedule Course Offering Details
                </TableCell>
                <TableCell
                  sx={{
                    width: "104px",
                    minWidth: "104px",
                  }}
                >
                  Course
                </TableCell>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={(e: React.MouseEvent<unknown>) =>
                    handleRequestSort(e, "firstSession")
                  }
                  fieldName="Date"
                  keyName="firstSession"
                  sxStyle={{
                    width: "150px",
                    minWidth: "150px",
                  }}
                />
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={(e: React.MouseEvent<unknown>) =>
                    handleRequestSort(e, "term")
                  }
                  fieldName="Term"
                  keyName="term"
                  sxStyle={{
                    width: "82px",
                    minWidth: "82px",
                  }}
                />
                <TableCell sx={{ width: "112px", minWidth: "112px" }}>
                  Educators
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ width: "78px", minWidth: "78px" }}
                >
                  No. Enrolled
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ width: "78px", minWidth: "78px" }}
                >
                  No. Attended
                </TableCell>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={(e: React.MouseEvent<unknown>) =>
                    handleRequestSort(e, "location")
                  }
                  fieldName="Location"
                  keyName="location"
                  sxStyle={{
                    width: "146px",
                    minWidth: "146px",
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduledOfferingList?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell
                    component="th"
                    scope="row"
                    className="sco-list-word-wrap"
                  >
                    <Box
                      title={
                        isEditAllowed ? "" : "You don't have permission to edit"
                      }
                      sx={{
                        cursor: isEditAllowed ? "pointer" : "not-allowed",
                        textDecoration: isEditAllowed ? "underline" : "none",
                      }}
                      onClick={() => {
                        if (
                          getPagePermissions(
                            user?.permissions,
                            AppPages.ScheduledOfferings
                          )?.edit
                        ) {
                          history.push({
                            pathname: AppRoutings.ScheduledOfferingsDetails,
                            state: {
                              scoId: Number(row.id),
                              autoScrollToSessions: false,
                            },
                          });
                        }
                      }}
                    >
                      {row.courseOfferingDetails}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" className="sco-list-word-wrap">
                      {row.courseDTO?.courseNo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.firstSession ? (
                        <>
                          <div>{row.firstSession.split(",")[0]}</div>
                          <div>{row.firstSession.split(",")[1]}</div>
                        </>
                      ) : (
                        "-"
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">{row.termsDTO?.termName}</TableCell>
                  <TableCell sx={{ wordWrap: "break-word" }}>
                    {row.educators || "-"}
                  </TableCell>
                  <TableCell align="center">{row.noEnrolled}</TableCell>
                  <TableCell align="center">{row.attendanceCount}</TableCell>
                  <TableCell>
                    <Typography variant="body2" className="sco-list-word-wrap">
                      {row.locationDTO?.location}
                      {row.locationDTO.isBlended &&
                        " - Blended (Face To Face / Online)"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {scheduledOfferingList && scheduledOfferingList.length > 0 ? (
          <CustomPagination
            handleChange={handleChange}
            page={page}
            pageNo={pageNo}
            onPageChange={onPageChange}
            listMeta={scheduledOfferingListMeta}
          />
        ) : (
          <NoRecords />
        )}
      </Card>
    </Grid>
  );
};

export default SCOTable;
